// Months as the API speaks them (`YYYY-MM`, UTC) and as people read them ("Mar 2026"), for the
// month pickers that replaced the typed YYYY-MM inputs. Pure; tests/unit/months.test.ts.

export type Month = string;

const SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;
const LONG = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

export const MONTH_SHORT_NAMES: readonly string[] = SHORT;

export function isMonth(value: string | null | undefined): value is Month {
  return typeof value === "string" && /^\d{4}-(0[1-9]|1[0-2])$/.test(value);
}

export function monthOf(year: number, monthIndex: number): Month {
  const total = year * 12 + monthIndex;
  const y = Math.floor(total / 12);
  const m = total - y * 12;
  return `${String(y).padStart(4, "0")}-${String(m + 1).padStart(2, "0")}`;
}

export function monthParts(month: Month): { year: number; index: number } {
  return { year: Number(month.slice(0, 4)), index: Number(month.slice(5, 7)) - 1 };
}

/** The UTC month of a date (Nexus bills in UTC months). */
export function currentMonth(now: Date): Month {
  return monthOf(now.getUTCFullYear(), now.getUTCMonth());
}

export function addMonths(month: Month, delta: number): Month {
  const { year, index } = monthParts(month);
  return monthOf(year, index + delta);
}

/** Negative when a is before b; months compare as strings. */
export function compareMonths(a: Month, b: Month): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** Inclusive number of months from `from` to `to` (1 for the same month; ≤ 0 when reversed). */
export function monthSpan(from: Month, to: Month): number {
  const a = monthParts(from);
  const b = monthParts(to);
  return (b.year - a.year) * 12 + (b.index - a.index) + 1;
}

/** Every month from `from` to `to`, inclusive, oldest first. */
export function monthsBetween(from: Month, to: Month): Month[] {
  const span = monthSpan(from, to);
  return Array.from({ length: Math.max(0, span) }, (_, i) => addMonths(from, i));
}

/** "Mar 2026". */
export function monthLabel(month: Month): string {
  const { year, index } = monthParts(month);
  return `${SHORT[index] ?? month} ${year}`;
}

/** "March 2026". */
export function longMonthLabel(month: Month): string {
  const { year, index } = monthParts(month);
  return `${LONG[index] ?? month} ${year}`;
}

/** "Mar" — the axis label under a bar. */
export function shortMonthLabel(month: Month): string {
  return SHORT[monthParts(month).index] ?? month;
}

/** "Mar 2026 – Aug 2026", or one month alone. */
export function rangeLabel(from: Month, to: Month): string {
  return from === to ? monthLabel(from) : `${monthLabel(from)} – ${monthLabel(to)}`;
}

export interface MonthRange {
  from: Month;
  to: Month;
}

/** The last `count` months ending with `end` (default the current month). */
export function lastMonths(count: number, end: Month): MonthRange {
  return { from: addMonths(end, -(Math.max(1, count) - 1)), to: end };
}

/** A range with its ends in order. */
export function orderedRange(a: Month, b: Month): MonthRange {
  return compareMonths(a, b) <= 0 ? { from: a, to: b } : { from: b, to: a };
}

/** Whether `month` lies within the optional bounds, inclusive. */
export function withinBounds(month: Month, min: Month | undefined, max: Month | undefined): boolean {
  if (min !== undefined && compareMonths(month, min) < 0) return false;
  if (max !== undefined && compareMonths(month, max) > 0) return false;
  return true;
}

/**
 * The range picker's click logic: the first click starts a range, the second ends it (in either
 * order). `pending` is the start chosen so far. A range longer than `maxSpan` keeps its new end
 * and moves the start so the span fits.
 */
export function pickRangeMonth(
  pending: Month | null,
  clicked: Month,
  maxSpan?: number,
): { pending: Month | null; range: MonthRange | null } {
  if (pending === null) return { pending: clicked, range: null };
  const range = orderedRange(pending, clicked);
  if (maxSpan !== undefined && monthSpan(range.from, range.to) > maxSpan) {
    return clicked === range.to
      ? { pending: null, range: { from: addMonths(range.to, -(maxSpan - 1)), to: range.to } }
      : { pending: null, range: { from: range.from, to: addMonths(range.from, maxSpan - 1) } };
  }
  return { pending: null, range };
}
